import { useState, useRef, useEffect, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { BsSearch, BsX, BsBuilding } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';

import { searchCompaniesByName, selectCompanyFromAutocomplete } from '../../../api/companyDetailApi';

const DEBOUNCE_MS = 300;

export const CompanySearchAutocomplete = () => {
	const [query, setQuery] = useState('');
	const [results, setResults] = useState([]);
	const [isOpen, setIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const anchorRef = useRef(null);
	const inputRef = useRef(null);
	const debounceRef = useRef(null);
	const navigate = useNavigate();

	// Debounced search
	const performSearch = useCallback(async (searchQuery) => {
		const q = searchQuery.trim();
		if (q.length < 2) {
			setResults([]);
			setIsOpen(false);
			return;
		}

		setIsLoading(true);
		try {
			const data = await searchCompaniesByName(q);
			setResults(data);
			setIsOpen(true);
		} catch (err) {
			console.error('Company search failed:', err);
			setResults([]);
		} finally {
			setIsLoading(false);
		}
	}, []);

	// Cleanup debounce on unmount
	useEffect(() => {
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, []);

	const [isSelecting, setIsSelecting] = useState(false);

	const handleSelect = async (company) => {
		setIsSelecting(true);
		setIsOpen(false);
		try {
			const globalCompanyId = await selectCompanyFromAutocomplete(company.id);
			setQuery('');
			setResults([]);
			navigate(`/leads/company/${globalCompanyId}`);
		} catch (err) {
			console.error('Failed to select company:', err);
		} finally {
			setIsSelecting(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Escape') {
			setIsOpen(false);
			inputRef.current?.blur();
		}
	};

	const handleChange = (e) => {
		const value = e.target.value;
		setQuery(value);

		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			performSearch(value);
		}, DEBOUNCE_MS);
	};

	const handleClear = () => {
		setQuery('');
		setResults([]);
		setIsOpen(false);
		inputRef.current?.focus();
	};

	const showEmptyState = !isLoading && isOpen && query.trim().length >= 2 && results.length === 0;
	const showResults = !isLoading && isOpen && results.length > 0;

	return (
		<div ref={anchorRef} className="relative w-full">
			<TextInput
				ref={inputRef}
				value={query}
				onChange={handleChange}
				onFocus={() => results.length > 0 && setIsOpen(true)}
				onKeyDown={handleKeyDown}
				disabled={isSelecting}
				placeholder="Firmenname eingeben…"
				className="pr-10"
			/>
			{query ? (
				<button
					onClick={handleClear}
					className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-bgBase-800 hover:text-text transition-colors"
				>
					<BsX className="w-5 h-5" />
				</button>
			) : (
				<BsSearch className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-bgBase-800 pointer-events-none" />
			)}

			<Popover
				open={isLoading || showResults || showEmptyState}
				onClose={() => setIsOpen(false)}
				anchorRef={anchorRef}
				align="stretch"
			>
				<Panel.Body padded={false}>
					{isLoading && (
						<div className="px-4 py-3 text-sm text-bgBase-800 text-center">
							<div className="inline-block animate-spin h-4 w-4 border-2 border-bgBase-400 border-t-primary-500 rounded-full mr-2 align-middle" />
							Suche…
						</div>
					)}

					{showResults && (
						<ul className="py-1">
							{results.map((company) => (
								<li key={company.id}>
									<button
										onClick={() => handleSelect(company)}
										className="w-full flex items-center gap-3 px-4 py-3 text-left text-text hover:bg-primary-100 hover:text-primary-700 transition-colors cursor-pointer"
									>
										<BsBuilding className="w-4 h-4 flex-shrink-0 text-bgBase-800" />
										<div className="min-w-0">
											<span className="block truncate font-medium">{company.companyName}</span>
											{company.addressCity && (
												<span className="block truncate text-xs text-bgBase-800">
													{company.addressCity}
												</span>
											)}
										</div>
									</button>
								</li>
							))}
						</ul>
					)}

					{showEmptyState && (
						<div className="px-4 py-4 text-sm text-bgBase-800 text-center">Keine Unternehmen gefunden</div>
					)}
				</Panel.Body>
			</Popover>
		</div>
	);
};
